import { OctagonPause } from 'lucide-react';
import { cn } from '../lib/cn';
import { useUiMessages } from '../i18n/messages';

export interface PauseExternalActionsBannerProps {
  /** When false the banner renders nothing — it must never be dismissible while true. */
  readonly active: boolean;
  readonly workspaceName: string;
  readonly pausedBy?: string | null;
  readonly pausedAt?: string | null;
  readonly reason?: string | null;
  readonly className?: string;
}

/**
 * Global kill-switch banner (design doc §10.5, §22.3). Renders no close
 * button by design — the only way it disappears is `active` becoming false,
 * i.e. an explicit resume action elsewhere in the product.
 */
export function PauseExternalActionsBanner({
  active,
  workspaceName,
  pausedBy,
  pausedAt,
  reason,
  className,
}: PauseExternalActionsBannerProps) {
  const messages = useUiMessages();
  if (!active) return null;
  return (
    <div
      role="alert"
      aria-live="assertive"
      className={cn(
        'flex w-full items-start gap-3 border-b border-red-900/20 bg-red-600 px-4 py-3 text-sm ' +
          'font-medium text-white',
        className,
      )}
    >
      <OctagonPause aria-hidden="true" className="mt-0.5 size-5 shrink-0" />
      <div className="flex flex-col gap-0.5">
        <p>{messages.pause.headline(<strong>{workspaceName}</strong>)}</p>
        <p className="text-red-100">
          {messages.pause.meta({
            pausedBy: pausedBy ?? null,
            pausedAt: pausedAt ?? null,
            reason: reason ?? null,
          })}
        </p>
      </div>
    </div>
  );
}
